'use client';

import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import type { AgentChatWidgetProps } from './types';
import type { FilterGroup } from '../search/types';
import { useAgentChat } from './hooks/useAgentChat';
import ChatHeader from '../chat/ChatHeader';
import AgentChatSidebar from './AgentChatSidebar';
import ChatProductGrid from '../chat/ChatProductGrid';
import AgentChatFiltersSidebar from './AgentChatFiltersSidebar';
import { useDismissOnOutsideTap } from '../search/useDismissOnOutsideTap';
import { useClientConfig } from '@/lib/clients/ClientContext';
import { isFacetHidden, priceFiltersEnabled } from '@/lib/clients/helpers';
import { groupIntoPriceBands } from '../search/searchHelpers';

const PRODUCTS_PER_PAGE = 100;

/** Price-band lookup table — must match the band ranges produced by
 *  groupIntoPriceBands in searchHelpers.ts. Used to translate sidebar item
 *  labels ↔ band range strings. */
const PRICE_BAND_TABLE = [
  { label: 'Under €20',   range: '0-20' },
  { label: '€20 - €40',   range: '20-40' },
  { label: '€40 - €60',   range: '40-60' },
  { label: '€60 - €100',  range: '60-100' },
  { label: '€100 - €150', range: '100-150' },
  { label: 'Over €150',   range: '150+' },
];

export default function AgentChatWidget({ onClose, onSwitchToSearch, viewMode, onViewModeChange }: AgentChatWidgetProps) {
  const clientConfig = useClientConfig();
  // Per-customer-pricing (B2B) clients get no price filter here either. This
  // sidebar filters in memory against the product's INDEXED price, which for
  // those clients is the catalogue RRP — not what the logged-in customer pays.
  // Same rule as the main search sidebar; see priceFiltersEnabled.
  const priceFiltersOn = priceFiltersEnabled(clientConfig);
  const {
    messages,
    products,
    searchKey,
    pinnedProductUrls,
    awaitingPicks,
    suggestions,
    isLoading,
    isToolCalling,
    sendMessage,
    handleChipClick,
  } = useAgentChat();

  // --- Filter state ---
  // `categories` and `brands` are special (categories = top-level + subcategory
  // merged in the sidebar; brands = the scalar `brand` field). Everything else
  // — every facetField in clientConfig.facetFields including color/material/
  // subcategory — lives in `dynamicFacets`, keyed by the field name. This
  // mirrors how the normal search FiltersSidebar handles all 50 kaizershop
  // facets generically via buildCustomFilterGroups.
  const [appliedFilters, setAppliedFilters] = useState<{
    categories: string[];
    brands: string[];
    dynamicFacets: Record<string, string[]>;
    /** Single price-band selection like "0-20", "20-40", or "150+" (range
     *  format from groupIntoPriceBands). null = no price filter. */
    priceRange: string | null;
  }>({ categories: [], brands: [], dynamicFacets: {}, priceRange: null });

  // --- Pagination + view mode state ---
  const [currentPage, setCurrentPage] = useState(1);
  // Filters slide-over for narrow chat (<xl), where the persistent filters
  // sidebar is hidden. Opened by the «Φίλτρα» button in the products header.
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  // Touch screens: a tap outside the open slide-over only closes it (see the hook).
  const filterDrawerRef = useRef<HTMLDivElement>(null);
  const closeFilterDrawer = useCallback(() => setFilterDrawerOpen(false), []);
  useDismissOnOutsideTap(filterDrawerRef, closeFilterDrawer, filterDrawerOpen);

  // Reset filters + page when products change (new agent search)
  const prevProductIdsRef = useRef('');
  useEffect(() => {
    const ids = products.map(p => p.id).slice(0, 5).join(',');
    if (ids !== prevProductIdsRef.current) {
      prevProductIdsRef.current = ids;
      setAppliedFilters({ categories: [], brands: [], dynamicFacets: {}, priceRange: null });
      setCurrentPage(1);
    }
  }, [products]);

  // Helper for parsing a price-band range string like "20-40" / "150+" into bounds.
  const parsePriceRange = (range: string): { min: number; max: number } => {
    const [minStr, maxStr] = range.split('-');
    const min = parseFloat(minStr);
    const max = maxStr === '+' || maxStr === undefined ? Infinity : parseFloat(maxStr);
    return { min, max };
  };

  // --- The assistant's picks: a row ABOVE the grid, not a reorder of it ---
  // Founder, 2026-11-03: re-sorting the grid when the picks arrive swapped
  // cards under the shopper's eyes. Now the grid keeps the search's order and
  // never moves; the picks (in the order the reply names them) fill a row of
  // placeholders above it. Shown always, not only for mixed-category results
  // as the old pinned section was: the row is where the picks live now.
  const picks = useMemo(() => {
    if (pinnedProductUrls.length === 0 || products.length === 0) return [];
    const byUrl = new Map(products.map((p) => [p.url, p]));
    // At most 3: the row holds 3 placeholders, and a 4th pick would push the
    // whole grid down a row.
    return pinnedProductUrls
      .map((url) => byUrl.get(url))
      .filter((p): p is (typeof products)[number] => !!p)
      .slice(0, 3);
  }, [products, pinnedProductUrls]);

  // --- Client-side filtering ---
  const hasFilters =
    appliedFilters.categories.length > 0 ||
    appliedFilters.brands.length > 0 ||
    appliedFilters.priceRange !== null ||
    Object.values(appliedFilters.dynamicFacets).some(arr => arr.length > 0);

  const filteredProducts = useMemo(() => {
    if (!hasFilters) return null;
    let filtered = products;
    if (appliedFilters.categories.length > 0) {
      // A "category" pick in the merged group could match either the scalar
      // `category` field or any value in the `subcategory[]` array.
      filtered = filtered.filter(p => {
        if (p.category != null && appliedFilters.categories.includes(p.category)) return true;
        if (Array.isArray(p.subcategory) && p.subcategory.some(s => appliedFilters.categories.includes(s))) return true;
        return false;
      });
    }
    if (appliedFilters.brands.length > 0) {
      filtered = filtered.filter(p => p.brand != null && appliedFilters.brands.includes(p.brand));
    }
    // Every other facet field — color, material, subcategory, product_type,
    // stock_status, screen_size, etc. — applied generically.
    for (const [field, selected] of Object.entries(appliedFilters.dynamicFacets)) {
      if (selected.length === 0) continue;
      filtered = filtered.filter(p => {
        const vals = (p as Record<string, unknown>)[field];
        return Array.isArray(vals) && vals.some(v => selected.includes(String(v)));
      });
    }
    // Price band filter (single-select) — apply [min, max) bound against the
    // product's numeric price.
    if (appliedFilters.priceRange) {
      const { min, max } = parsePriceRange(appliedFilters.priceRange);
      filtered = filtered.filter(p => {
        const price = typeof p.price === 'number' ? p.price : parseFloat(String(p.price));
        return !isNaN(price) && price >= min && price < max;
      });
    }
    return filtered;
  }, [products, appliedFilters, hasFilters]);

  // Build filter groups with cross-filtering counts
  // Each facet group counts products filtered by ALL OTHER active filters (not its own)
  const filterGroups = useMemo<FilterGroup[]>(() => {
    if (products.length === 0) return [];

    // Helper: apply all active filters EXCEPT the one for `excludeKey`.
    // `excludeKey` is one of: 'categories' / 'brands' / a dynamicFacets field
    // name. Used for cross-filtering counts (each facet's counts reflect what
    // OTHER active filters would select).
    const applyOtherFilters = (excludeKey: string) => {
      let ps = products;
      if (excludeKey !== 'categories' && appliedFilters.categories.length > 0) {
        ps = ps.filter(p => {
          if (p.category != null && appliedFilters.categories.includes(p.category)) return true;
          if (Array.isArray(p.subcategory) && p.subcategory.some(s => appliedFilters.categories.includes(s))) return true;
          return false;
        });
      }
      if (excludeKey !== 'brands' && appliedFilters.brands.length > 0) {
        ps = ps.filter(p => p.brand != null && appliedFilters.brands.includes(p.brand));
      }
      for (const [field, selected] of Object.entries(appliedFilters.dynamicFacets)) {
        if (field === excludeKey) continue;
        if (selected.length === 0) continue;
        ps = ps.filter(p => {
          const vals = (p as Record<string, unknown>)[field];
          return Array.isArray(vals) && vals.some(v => selected.includes(String(v)));
        });
      }
      if (excludeKey !== 'priceRange' && appliedFilters.priceRange) {
        const { min, max } = parsePriceRange(appliedFilters.priceRange);
        ps = ps.filter(p => {
          const price = typeof p.price === 'number' ? p.price : parseFloat(String(p.price));
          return !isNaN(price) && price >= min && price < max;
        });
      }
      return ps;
    };

    const groups: FilterGroup[] = [];

    // Merge categories + subcategories into one group, sorted by count.
    // Cross-filtering: when counting this group, exclude both `categories`
    // (the merged top-level) and any active `subcategory` selection.
    const catSubProducts = products.filter(p => {
      if (appliedFilters.brands.length > 0 && (p.brand == null || !appliedFilters.brands.includes(p.brand))) return false;
      for (const [field, selected] of Object.entries(appliedFilters.dynamicFacets)) {
        if (field === 'subcategory') continue; // subcategory selection is part of the categories group
        if (selected.length === 0) continue;
        const vals = (p as Record<string, unknown>)[field];
        if (!Array.isArray(vals) || !vals.some(v => selected.includes(String(v)))) return false;
      }
      return true;
    });
    const catItems: { name: string; count: number; key?: string }[] = [];
    const catCounts = new Map<string, number>();
    for (const p of catSubProducts) {
      if (p.category) catCounts.set(p.category, (catCounts.get(p.category) || 0) + 1);
    }
    catItems.push(...Array.from(catCounts.entries()).map(([name, count]) => ({ name, count })));
    const subCounts = new Map<string, number>();
    for (const p of catSubProducts) {
      if (p.subcategory) for (const s of p.subcategory) subCounts.set(s, (subCounts.get(s) || 0) + 1);
    }
    // Exclude subcategories whose name already appears as a category (avoid duplicates)
    const catNameSet = new Set(catCounts.keys());
    catItems.push(...Array.from(subCounts.entries())
      .filter(([name]) => !catNameSet.has(name))
      .map(([name, count]) => ({ name, count, key: `sub:${name}` })));
    if (catItems.length > 0) {
      catItems.sort((a, b) => b.count - a.count);
      groups.push({
        title: clientConfig.ui.strings.otherCategories,
        items: catItems,
        maxVisible: 5,
      });
    }

    const brandCounts = new Map<string, number>();
    for (const p of applyOtherFilters('brands')) {
      if (p.brand) brandCounts.set(p.brand, (brandCounts.get(p.brand) || 0) + 1);
    }
    if (brandCounts.size > 0) {
      groups.push({
        title: clientConfig.ui.strings.brandLabel,
        items: Array.from(brandCounts.entries())
          .sort((a, b) => b[1] - a[1])
          .map(([name, count]) => ({ name, count })),
        maxVisible: 5,
      });
    }

    // ALL dynamic facet fields from client config — color, material,
    // product_type, stock_status, screen_size, compatible_*, etc. Sidebar
    // shows only facets with ≥1 value in the current result set, so a long
    // facetFields list is safe (empty ones are skipped naturally).
    for (const ff of clientConfig.facetFields) {
      if (!ff.isArray) continue;
      if (ff.field === 'subcategory') continue; // already merged into categories group above
      if (isFacetHidden(clientConfig, ff.field)) continue;
      const counts = new Map<string, number>();
      for (const p of applyOtherFilters(ff.field)) {
        const val = (p as Record<string, unknown>)[ff.field];
        if (Array.isArray(val)) for (const v of val) counts.set(String(v), (counts.get(String(v)) || 0) + 1);
      }
      if (counts.size > 0) {
        groups.push({
          title: ff.label,
          items: Array.from(counts.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([name, count]) => ({ name, count })),
          maxVisible: 5,
        });
      }
    }

    // Εύρος Τιμής — banded price filter, computed from product.price across
    // the current result set (excluding the price filter itself, so the
    // bands always show how many products would match each band). Bands are
    // the same fixed buckets the normal search uses (groupIntoPriceBands).
    // B2C only: product.price is the catalogue price, which is the real price
    // for B2C and the WRONG one for per-customer-pricing clients.
    if (priceFiltersOn) {
      const priceCountsByExactValue: Record<string, number> = {};
      for (const p of applyOtherFilters('priceRange')) {
        const price = typeof p.price === 'number' ? p.price : parseFloat(String(p.price));
        if (!isNaN(price)) {
          const key = String(price);
          priceCountsByExactValue[key] = (priceCountsByExactValue[key] || 0) + 1;
        }
      }
      const bands = groupIntoPriceBands(priceCountsByExactValue);
      if (bands.length > 0) {
        groups.push({
          title: clientConfig.ui.strings.priceRange,
          items: bands.map(b => ({ name: b.label, count: b.count, key: b.range })),
          maxVisible: 6,
        });
      }
    }

    return groups;
  }, [products, appliedFilters, priceFiltersOn]);

  // All selected filter items for sidebar highlight. Subcategory selections
  // get the `sub:` prefix because they appear under the merged categories
  // group in the UI (where prefix disambiguates from top-level categories).
  const allSelectedFilterItems = useMemo(() => {
    const items: string[] = [...appliedFilters.categories, ...appliedFilters.brands];
    for (const [field, selected] of Object.entries(appliedFilters.dynamicFacets)) {
      if (field === 'subcategory') {
        items.push(...selected.map(s => `sub:${s}`));
      } else {
        items.push(...selected);
      }
    }
    // For price the sidebar item is keyed by band label (`name`); we look up
    // the active label from the table so the sidebar highlights the band.
    if (appliedFilters.priceRange) {
      const activeBand = PRICE_BAND_TABLE.find(b => b.range === appliedFilters.priceRange);
      if (activeBand) items.push(activeBand.label);
    }
    return items;
  }, [appliedFilters]);

  // --- Filter toggle handler ---
  // Routes the toggled item into the right bucket of appliedFilters:
  //   - Brand label  → appliedFilters.brands
  //   - "sub:" prefix → appliedFilters.dynamicFacets.subcategory
  //   - A configured facet's label → appliedFilters.dynamicFacets[field]
  //   - Otherwise (top-level category)  → appliedFilters.categories
  const handleFilterToggle = useCallback((groupTitle: string, itemName: string) => {
    const toggleArr = (arr: string[], v: string): string[] =>
      arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];

    if (groupTitle === clientConfig.ui.strings.priceRange) {
      // Single-select: clicking the same band deselects, clicking a different
      // band replaces. itemName is the band label; look up the range key.
      const band = PRICE_BAND_TABLE.find(b => b.label === itemName);
      if (band) {
        setAppliedFilters(prev => ({
          ...prev,
          priceRange: prev.priceRange === band.range ? null : band.range,
        }));
      }
    } else if (groupTitle === clientConfig.ui.strings.brandLabel) {
      setAppliedFilters(prev => ({ ...prev, brands: toggleArr(prev.brands, itemName) }));
    } else if (itemName.startsWith('sub:')) {
      const sub = itemName.slice(4);
      setAppliedFilters(prev => {
        const cur = prev.dynamicFacets.subcategory || [];
        return {
          ...prev,
          dynamicFacets: { ...prev.dynamicFacets, subcategory: toggleArr(cur, sub) },
        };
      });
    } else {
      // Match against any configured facet field by its label
      const facetField = clientConfig.facetFields.find(f => f.label === groupTitle);
      if (facetField && facetField.field !== 'subcategory') {
        setAppliedFilters(prev => {
          const cur = prev.dynamicFacets[facetField.field] || [];
          return {
            ...prev,
            dynamicFacets: { ...prev.dynamicFacets, [facetField.field]: toggleArr(cur, itemName) },
          };
        });
      } else {
        // Default: it's a top-level category from the merged categories group
        setAppliedFilters(prev => ({ ...prev, categories: toggleArr(prev.categories, itemName) }));
      }
    }
    setCurrentPage(1);
  }, [clientConfig]);

  // --- Pagination ---
  const activeProducts = filteredProducts ?? products;
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return activeProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [activeProducts, currentPage]);

  const totalPages = Math.ceil(activeProducts.length / PRODUCTS_PER_PAGE);

  // The picks row belongs to the unfiltered first page: under a filter the
  // picks may not match it, and page 2 is "more of the same". Three results or
  // fewer already fit in one row, so a picks row would only repeat them.
  const picksRowOn = !hasFilters && currentPage === 1 && products.length > 3;

  // The grid's "which results" key: a new search, filter or page. Pinning the
  // assistant's picks is NOT a new set, even when a pick from page 2 moves
  // onto page 1 (that used to replay the whole page's entrance animation).
  const gridResultsKey = useMemo(
    () => `${searchKey}|${currentPage}|${JSON.stringify(appliedFilters)}`,
    [searchKey, currentPage, appliedFilters],
  );

  const productGridRef = useRef<HTMLDivElement>(null);
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    productGridRef.current?.scrollTo({ top: 0 });
  }, []);

  // Phone (<md) only: the conversation is a slide-over rather than a column, so
  // it needs an open/closed state. Defaults OPEN because entering chat mode is
  // itself the request to talk — the shopper taps «Ρωτήστε την Αριάδνη» and
  // expects the conversation, not a grid. At md+ this is ignored entirely.
  const [chatPanelOpen, setChatPanelOpen] = useState(true);

  // Stable handlers: ChatProductGrid is memoised, so it skips the re-renders
  // the streaming reply causes here — only if its props stay the same.
  const toggleFilterDrawer = useCallback(() => setFilterDrawerOpen((o) => !o), []);
  const toggleChatPanel = useCallback(() => setChatPanelOpen((o) => !o), []);

  return (
    <div className="flex flex-col h-full bg-white">
      <ChatHeader onClose={onClose} onSwitchToSearch={onSwitchToSearch} />

      {/* Three-column layout */}
      <div className="flex flex-1 min-h-0">
        {/* Left: Filters sidebar — only show when we have products */}
        {filterGroups.length > 0 && (
          <div className="hidden xl:block flex-shrink-0 w-[280px] border-r border-[#E5E7EB]">
            <AgentChatFiltersSidebar
              groups={filterGroups}
              selectedItems={allSelectedFilterItems}
              onFilterToggle={handleFilterToggle}
            />
          </div>
        )}

        {/* Center: Product grid. Relative, non-scrolling wrapper so the filters
            slide-over can overlay the products (which scroll inside it). */}
        <div className="flex-1 min-w-0 relative">
          <div ref={productGridRef} className="absolute inset-0 overflow-y-auto overscroll-contain custom-scrollbar">
            <ChatProductGrid
              products={paginatedProducts}
              resultsKey={gridResultsKey}
              picks={picksRowOn ? picks : undefined}
              picksPending={picksRowOn && awaitingPicks && !clientConfig.ui.chatPicksOnRequest}
              isLoading={isToolCalling}
              totalCount={activeProducts.length}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              viewMode={viewMode}
              onViewModeChange={onViewModeChange}
              onOpenFilters={filterGroups.length > 0 ? toggleFilterDrawer : undefined}
              filtersOpen={filterDrawerOpen}
              activeFilterCount={allSelectedFilterItems.length}
              onToggleChat={toggleChatPanel}
              chatOpen={chatPanelOpen}
            />
          </div>

          {/* Filters slide-over (tablet/mobile, <xl) — overlays the products from
              the left, pinned below the products header (top-[84px]); reuses the
              chat filters sidebar. The persistent xl sidebar is used at xl+. */}
          {filterDrawerOpen && filterGroups.length > 0 && (
            <div ref={filterDrawerRef} className="xl:hidden absolute left-0 top-[84px] bottom-0 w-[280px] max-w-[85%] z-30 overflow-hidden shadow-[2px_2px_4px_0px_rgba(0,0,0,0.1)]">
              <div className="h-full animate-slideInLeft">
                <AgentChatFiltersSidebar
                  groups={filterGroups}
                  selectedItems={allSelectedFilterItems}
                  onFilterToggle={handleFilterToggle}
                  onCollapse={closeFilterDrawer}
                />
              </div>
            </div>
          )}

          {/* Conversation (phone, <md) — FULL SCREEN over the products, below
              the chat header. It used to be a 340px panel pinned right under
              the products toolbar (top-[84px]) so a strip of the grid showed
              at the left; before the first answer there IS no grid, so the
              shopper saw an empty 90px strip and an empty 84px band around a
              narrow chat (syfak, iPhone, 2026-10-27). The way between the two
              is explicit instead: «Προϊόντα (N)» in the chat header, and the
              chat button in the products toolbar.
              No slide-in: it used to enter from the right (slideInRight,
              280ms), which on opening the chat read as the page drifting
              sideways until it settled (founder, iPhone, 2026-11-03). It now
              simply appears, both on opening and when switching back from the
              products. */}
          {chatPanelOpen && (
            <div className="md:hidden absolute inset-0 z-30 overflow-hidden bg-white">
              <div className="h-full">
                <AgentChatSidebar
                  messages={messages}
                  isLoading={isLoading}
                  suggestions={suggestions}
                  onSendMessage={sendMessage}
                  onChipClick={handleChipClick}
                  collapseLabel={`${clientConfig.ui.strings.productsTitle} (${activeProducts.length})`}
                  // Collapse is offered ONLY when there are products behind the
                  // panel. ChatProductGrid early-returns its whole header — and
                  // therefore the toolbar's chat toggle — when the result set is
                  // empty, so allowing collapse before the first answer would
                  // leave the shopper on an empty screen with no way back into
                  // the conversation. Same condition on both sides, so the
                  // collapse control and the way back can never disagree.
                  onCollapse={activeProducts.length > 0 ? () => setChatPanelOpen(false) : undefined}
                />
              </div>
            </div>
          )}
        </div>

        {/* Right: chat — a permanent column from `md` up, EXACTLY as before.
            Below md it is hidden here and rendered as the slide-over above,
            because a fixed 340px column left the product grid 35px wide on a
            375px phone (measured) — unreadable, one word per line. */}
        <div className="hidden md:flex flex-shrink-0">
          <AgentChatSidebar
            messages={messages}
            isLoading={isLoading}
            suggestions={suggestions}
            onSendMessage={sendMessage}
            onChipClick={handleChipClick}
          />
        </div>
      </div>
    </div>
  );
}
